import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QuoteTracker } from './QuoteTracker';
import { DeliveriesTable, useOpenDeliveries } from './Deliveries';
import { api, qs } from '../lib/api';
import { fmtMoney, fmtNumber, fmtRelative } from '../lib/format';
import { Button, Empty, ErrorState, SkeletonRows, useConfirm, useToast } from './ui';
import { IconPlus, IconTrash } from './icons';

export type OrderRow = { preparing?: boolean; prepNote?: string | null; id: number; customerId: number; customerName: string; documentType: string; typeLabel: string; status: 'draft' | 'issuing' | 'issued' | 'failed' | 'cancelled'; lines: number; morningNumber?: string | null; issuedEnvironment?: 'sandbox' | 'production' | null; total: number; updatedAt: string; createdBy: string | null };

/** Creates a draft (optionally copied from a past document) and opens the editor. */
export function useStartOrder() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const start = async (customerId: number, sourceDocumentId?: number, productIds?: number[]) => {
    setBusy(true);
    try {
      const r = await api<{ id: number }>('/api/orders', {
        body: { customerId, sourceDocumentId: sourceDocumentId ?? null, ...(productIds?.length ? { products: productIds.map((productId) => ({ productId, quantity: 1 })) } : {}) },
      });
      qc.invalidateQueries({ queryKey: ['orders'] });
      navigate(`/orders/${r.id}`);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return { start, busy };
}

export function NewOrderButton({ customerId, sourceDocumentId, children, size, variant }: { customerId: number; sourceDocumentId?: number; children: React.ReactNode; size?: 'sm'; variant?: 'primary' | 'default' }) {
  const { start, busy } = useStartOrder();
  return (
    <Button size={size} variant={variant} loading={busy} onClick={() => start(customerId, sourceDocumentId)}>
      {children}
    </Button>
  );
}

const STATUS = { draft: { label: 'טיוטה', tone: 'warn' }, issuing: { label: 'בהפקה…', tone: 'info' }, issued: { label: 'הופק', tone: 'ok' }, failed: { label: 'לבדיקה — לא ידוע אם הופק', tone: 'bad' }, cancelled: { label: 'בוטל', tone: '' } } as const;

/** Orders table — of one customer (with its branches) or of everyone. A row opens the order. */
export function OrdersTable({ customerId, status = 'draft', showCustomer }: { customerId?: number; status?: 'draft' | 'all'; showCustomer?: boolean }) {
  const { data, error, refetch } = useQuery({
    queryKey: ['orders', customerId ?? 'all', status],
    queryFn: () => api<{ items: OrderRow[] }>(`/api/orders${qs({ customerId, status })}`),
  });
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const qc = useQueryClient();
  const remove = async (o: OrderRow) => {
    if (!(await confirm({ title: `למחוק את הטיוטה של ${o.customerName}?`, body: `${o.typeLabel} · ${o.lines} שורות · ${fmtMoney(o.total)}. הטיוטה נמחקת לגמרי.`, confirmLabel: 'מחיקה', danger: true }))) return;
    try {
      await api(`/api/orders/${o.id}`, { method: 'DELETE' });
      qc.invalidateQueries({ queryKey: ['orders'] });
      toast.success('הטיוטה נמחקה');
    } catch (e) {
      toast.error(e);
    }
  };
  const togglePreparing = async (o: OrderRow) => {
    try {
      await api(`/api/orders/${o.id}/preparing`, { body: { preparing: !o.preparing } });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['deliveries'] });
      toast.success(o.preparing ? 'הוסר מההזמנות לאספקה' : 'נוסף להזמנות לאספקה (בהכנה)');
    } catch (e) {
      toast.error(e);
    }
  };
  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;
  const cols = showCustomer ? 7 : 6;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {showCustomer ? <th>לקוח</th> : null}
            <th>סוג</th>
            <th className="num">שורות</th>
            <th className="num">סה״כ כולל מע״מ</th>
            <th>סטטוס</th>
            <th className="hide-sm">עודכן</th>
            <th style={{ width: 1 }} />
          </tr>
        </thead>
        <tbody>
          {!data ? (
            <SkeletonRows rows={3} cols={cols} />
          ) : data.items.length === 0 ? (
            <tr>
              <td colSpan={cols}>
                <Empty
                  title={status === 'draft' ? 'אין טיוטות פתוחות' : 'אין הזמנות'}
                  body={customerId ? 'הזמנה חדשה נפתחת מהכפתור "הזמנה חדשה" או "הזמן שוב" במסמך קודם.' : 'הזמנה חדשה נפתחת מכרטיס הלקוח. טיוטה בלי מוצרים לא מוצגת ונמחקת לבד אחרי יום.'}
                />
              </td>
            </tr>
          ) : (
            data.items.map((o) => (
              <tr key={o.id} className="clickable" onClick={() => navigate(`/orders/${o.id}`)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate(`/orders/${o.id}`)}>
                {showCustomer ? <td className="primary">{o.customerName}</td> : null}
                <td>{o.typeLabel}</td>
                <td className="num tabular">{fmtNumber(o.lines)}</td>
                <td className="num tabular">{fmtMoney(o.total)}</td>
                <td>
                  <span className={`badge ${STATUS[o.status].tone}`}>{STATUS[o.status].label}</span>
                  {o.status === 'draft' && o.preparing ? <span className="badge warn" style={{ marginInlineStart: 4 }} title={o.prepNote ?? undefined}>בהכנה</span> : null}
                  {o.morningNumber ? <span className="small muted"> {o.morningNumber}{o.issuedEnvironment === 'sandbox' ? ' (Sandbox)' : ''}</span> : null}
                </td>
                <td className="hide-sm small muted">{fmtRelative(o.updatedAt)}{o.createdBy ? ` · ${o.createdBy}` : ''}</td>
                <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                  <Button size="sm" variant="primary" onClick={() => navigate(`/orders/${o.id}`)}>{o.status === 'draft' ? 'המשך' : 'פתיחה'}</Button>
                  {o.status === 'draft' ? (
                    <Button size="sm" variant="ghost" onClick={() => togglePreparing(o)} title="טיוטה בהכנה מופיעה בהזמנות לאספקה עוד לפני שהופק מסמך">
                      {o.preparing ? 'הסרה מהכנה' : 'להכנה'}
                    </Button>
                  ) : null}
                  {o.status === 'draft' ? (
                    <Button size="sm" variant="ghost" icon onClick={() => remove(o)} aria-label="מחיקת טיוטה" title="מחיקת הטיוטה"><IconTrash size={14} /></Button>
                  ) : null}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function CustomerOrders({ customerId }: { customerId: number }) {
  return (
    <section className="panel">
      <div className="panel-head row">
        <h2>הזמנות</h2>
        <span className="spacer" />
        <NewOrderButton customerId={customerId} size="sm" variant="primary"><IconPlus /> הזמנה חדשה</NewOrderButton>
      </div>
      <OrdersTable customerId={customerId} status="all" />
    </section>
  );
}

/** The customer's orders still to deliver and quotes that went out, in the customer file (orders tab). */
export function CustomerQuotes({ customerId }: { customerId: number }) {
  return (
    <>
      <section className="panel">
        <DeliveriesTable customerId={customerId} />
      </section>
      <section className="panel">
        <QuoteTracker customerId={customerId} />
      </section>
    </>
  );
}

type Tab = 'deliveries' | 'drafts' | 'quotes';

/**
 * The "הזמנות" page: what is open right now — orders to deliver, quotes waiting for an answer or
 * approved and waiting for an invoice, and drafts in progress.
 */
export function OrdersPage() {
  const [tab, setTab] = useState<Tab>('deliveries');
  const drafts = useQuery({ queryKey: ['orders', 'all', 'draft'], queryFn: () => api<{ items: OrderRow[] }>('/api/orders?status=draft') });
  const deliveries = useOpenDeliveries();
  const quotes = useQuery({
    queryKey: ['quote-tracking', 'all'],
    queryFn: () => api<{ counts: { waiting: number; approved: number; remindDue: number }; totals: { waiting: number; approved: number } }>('/api/orders/quotes'),
  });
  const dItems = [...(deliveries.data?.items ?? []), ...(deliveries.data?.preparing ?? [])];
  const late = dItems.filter((d) => d.daysOpen >= 3).length;
  const q = quotes.data;
  const tile = (key: Tab, label: string, value: number | undefined, meta: string, tone?: 'warn' | 'ok') => (
    <button key={label} className={`kpi kpi-button ${tab === key ? 'active' : ''}`} aria-pressed={tab === key} onClick={() => setTab(key)}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={tone && value ? { color: `var(--${tone})` } : undefined}>{value ?? '—'}</div>
      <div className="kpi-meta">{meta}</div>
    </button>
  );
  return (
    <>
      <div className="page-head">
        <div>
          <h1>הזמנות</h1>
          <div className="sub">מה פתוח עכשיו: הזמנות שיצאו וממתינות לאספקה, הצעות מחיר עד שהלקוח מאשר ומופקת חשבונית, וטיוטות בעבודה. הזמנה חדשה נפתחת מכרטיס הלקוח.</div>
        </div>
      </div>
      <div className="stack">
        <section className="kpis" aria-label="מצב ההזמנות">
          {tile('deliveries', 'הזמנות לאספקה', deliveries.data ? dItems.length : undefined, deliveries.data ? `${fmtMoney(dItems.reduce((s, d) => s + (d.total ?? 0), 0), { round: true })}${late ? ` · ${late} מעל 3 ימים` : ''}` : '', 'warn')}
          {tile('quotes', 'הצעות שאושרו — להפקה', q?.counts.approved, q ? fmtMoney(q.totals.approved, { round: true }) : '', 'ok')}
          {tile('quotes', 'הצעות ממתינות לאישור', q?.counts.waiting, q ? `${fmtMoney(q.totals.waiting, { round: true })}${q.counts.remindDue ? ` · ${q.counts.remindDue} לתזכורת` : ''}` : '')}
          {tile('drafts', 'טיוטות בעבודה', drafts.data?.items.length, 'הזמנות שהתחלת ועוד לא הופקו')}
        </section>
        <section className="panel">
          {tab === 'deliveries' ? <DeliveriesTable showCustomer /> : null}
          {tab === 'quotes' ? <QuoteTracker showCustomer /> : null}
          {tab === 'drafts' ? (
            <>
              <div className="panel-head"><h2>טיוטות בעבודה</h2></div>
              <OrdersTable showCustomer />
            </>
          ) : null}
        </section>
      </div>
    </>
  );
}
